// Password strength validation utilities

export interface PasswordStrengthResult {
  score: number; // 0-4 (0 = very weak, 4 = very strong)
  feedback: string[];
  isValid: boolean;
  requirements: PasswordRequirement[];
}

export interface PasswordRequirement {
  id: string;
  description: string;
  met: boolean;
  required: boolean;
}

export interface PasswordValidationConfig {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  maxLength?: number;
  commonPasswordsCheck: boolean;
}

// Default password requirements
const DEFAULT_CONFIG: PasswordValidationConfig = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: false,
  maxLength: 128,
  commonPasswordsCheck: true,
};

// Common weak passwords to check against
const COMMON_PASSWORDS = [
  'password',
  '123456',
  '123456789',
  'qwerty',
  'abc123',
  'password123',
  'admin',
  'letmein',
  'welcome',
  'monkey',
  '1234567890',
  'password1',
  'qwerty123',
  'welcome123',
  'admin123',
  'root',
  'toor',
  'pass',
  'test',
  'guest',
  'user',
  'login',
  'changeme',
  'newpassword',
];

// Special characters for validation
const SPECIAL_CHARS = /[!@#$%^&*()_+\-=[]{};':"\\|,.<>\/?]/;

export class PasswordValidator {
  private config: PasswordValidationConfig;

  constructor(config: Partial<PasswordValidationConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // Main validation method
  validatePassword(password: string): PasswordStrengthResult {
    const requirements = this.checkRequirements(password);
    const score = this.calculateScore(password);
    const feedback = this.generateFeedback(password, requirements, score);
    const isValid = requirements
      .filter(req => req.required)
      .every(req => req.met);

    return {
      score,
      feedback,
      isValid,
      requirements,
    };
  }

  // Check all password requirements
  private checkRequirements(password: string): PasswordRequirement[] {
    const requirements: PasswordRequirement[] = [];

    // Length requirement
    requirements.push({
      id: 'length',
      description: `At least ${this.config.minLength} characters`,
      met: password.length >= this.config.minLength,
      required: true,
    });

    // Maximum length requirement
    if (this.config.maxLength) {
      requirements.push({
        id: 'maxLength',
        description: `No more than ${this.config.maxLength} characters`,
        met: password.length <= this.config.maxLength,
        required: true,
      });
    }

    // Uppercase requirement
    if (this.config.requireUppercase) {
      requirements.push({
        id: 'uppercase',
        description: 'At least one uppercase letter',
        met: /[A-Z]/.test(password),
        required: true,
      });
    }

    // Lowercase requirement
    if (this.config.requireLowercase) {
      requirements.push({
        id: 'lowercase',
        description: 'At least one lowercase letter',
        met: /[a-z]/.test(password),
        required: true,
      });
    }

    // Numbers requirement
    if (this.config.requireNumbers) {
      requirements.push({
        id: 'numbers',
        description: 'At least one number',
        met: /\d/.test(password),
        required: true,
      });
    }

    // Special characters requirement
    if (this.config.requireSpecialChars) {
      requirements.push({
        id: 'specialChars',
        description: 'At least one special character (!@#$%^&*)',
        met: SPECIAL_CHARS.test(password),
        required: true,
      });
    }

    // Common passwords check
    if (this.config.commonPasswordsCheck) {
      requirements.push({
        id: 'commonPassword',
        description: 'Not a commonly used password',
        met: !this.isCommonPassword(password),
        required: true,
      });
    }

    // Additional strength checks (not required but improve score)
    requirements.push({
      id: 'noRepeating',
      description: 'No repeating characters (aaa, 111)',
      met: !this.hasRepeatingChars(password),
      required: false,
    });

    requirements.push({
      id: 'noSequential',
      description: 'No sequential characters (abc, 123)',
      met: !this.hasSequentialChars(password),
      required: false,
    });

    requirements.push({
      id: 'mixedCase',
      description: 'Good mix of uppercase and lowercase',
      met: this.hasMixedCase(password),
      required: false,
    });

    return requirements;
  }

  // Calculate password strength score (0-4)
  private calculateScore(password: string): number {
    let score = 0;

    // Base score from length
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Score from character variety
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecial = SPECIAL_CHARS.test(password);

    const varietyCount = [hasUpper, hasLower, hasNumbers, hasSpecial].filter(
      Boolean
    ).length;
    if (varietyCount >= 3) score += 1;
    if (varietyCount >= 4) score += 1;

    // Deduct points for common issues
    if (this.isCommonPassword(password)) score -= 2;
    if (this.hasRepeatingChars(password)) score -= 1;
    if (this.hasSequentialChars(password)) score -= 1;

    // Ensure score is within bounds
    return Math.max(0, Math.min(4, score));
  }

  // Generate feedback messages
  private generateFeedback(
    password: string,
    requirements: PasswordRequirement[],
    score: number
  ): string[] {
    const feedback: string[] = [];

    // Add feedback for unmet required requirements
    const unmetRequired = requirements.filter(req => req.required && !req.met);
    unmetRequired.forEach(req => {
      feedback.push(req.description);
    });

    // Add general strength feedback
    if (score === 0) {
      feedback.push(
        'Very weak password - consider using a longer, more complex password'
      );
    } else if (score === 1) {
      feedback.push('Weak password - add more character variety');
    } else if (score === 2) {
      feedback.push('Fair password - could be stronger');
    } else if (score === 3) {
      feedback.push('Good password strength');
    } else if (score === 4) {
      feedback.push('Excellent password strength');
    }

    // Add specific improvement suggestions
    if (password.length < 12) {
      feedback.push('Consider using a longer password for better security');
    }

    if (!SPECIAL_CHARS.test(password)) {
      feedback.push('Adding special characters (!@#$%^&*) increases security');
    }

    return feedback;
  }

  // Check if password is commonly used
  private isCommonPassword(password: string): boolean {
    const lowerPassword = password.toLowerCase();
    return COMMON_PASSWORDS.some(
      common => lowerPassword.includes(common) || common.includes(lowerPassword)
    );
  }

  // Check for repeating characters
  private hasRepeatingChars(password: string): boolean {
    return /(.)\1{2,}/.test(password); // 3 or more repeating characters
  }

  // Check for sequential characters
  private hasSequentialChars(password: string): boolean {
    const sequences = [
      'abcdefghijklmnopqrstuvwxyz',
      'qwertyuiopasdfghjklzxcvbnm',
      '1234567890',
    ];

    for (const sequence of sequences) {
      for (let i = 0; i <= sequence.length - 3; i++) {
        const subseq = sequence.substring(i, i + 3);
        if (password.toLowerCase().includes(subseq)) {
          return true;
        }
      }
    }

    return false;
  }

  // Check for good mix of uppercase and lowercase
  private hasMixedCase(password: string): boolean {
    const upperCount = (password.match(/[A-Z]/g) || []).length;
    const lowerCount = (password.match(/[a-z]/g) || []).length;
    const totalLetters = upperCount + lowerCount;

    if (totalLetters === 0) return false;

    // Good mix means neither case dominates too much
    const upperRatio = upperCount / totalLetters;
    return upperRatio > 0.1 && upperRatio < 0.9;
  }

  // Get password strength label
  static getStrengthLabel(score: number): string {
    switch (score) {
      case 0:
        return 'Very Weak';
      case 1:
        return 'Weak';
      case 2:
        return 'Fair';
      case 3:
        return 'Good';
      case 4:
        return 'Excellent';
      default:
        return 'Unknown';
    }
  }

  // Get password strength color (for UI)
  static getStrengthColor(score: number): string {
    switch (score) {
      case 0:
        return '#ff4444'; // Red
      case 1:
        return '#ff8800'; // Orange
      case 2:
        return '#ffbb00'; // Yellow
      case 3:
        return '#88cc00'; // Light Green
      case 4:
        return '#00cc44'; // Green
      default:
        return '#cccccc'; // Gray
    }
  }
}

// Export default instance
export const passwordValidator = new PasswordValidator();

// Utility function for quick validation
export const validatePassword = (
  password: string,
  config?: Partial<PasswordValidationConfig>
) => {
  const validator = config ? new PasswordValidator(config) : passwordValidator;
  return validator.validatePassword(password);
};

// Utility function to check if password meets minimum requirements
export const isPasswordValid = (
  password: string,
  config?: Partial<PasswordValidationConfig>
): boolean => {
  const result = validatePassword(password, config);
  return result.isValid;
};
